import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { UserPlus, Copy, Mail, Users, MoreVertical, UserMinus, ListPlus, Check, AlertTriangle, Upload, FileSpreadsheet, Wallet, ShieldCheck } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { PageHeader, UserAvatar, EmptyState, Spinner } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { fmtDate, fmtMoney, currencySymbol } from "@/lib/constants";
import { AdminsPanel, BudgetSummary, BudgetCell } from "./employees/panels";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseBulk(text) {
  return text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((line) => {
    const parts = line.split(/[,;\t]+/).map((p) => p.trim()).filter(Boolean);
    const email = (parts.find((p) => EMAIL_RE.test(p)) || "").toLowerCase();
    const name = parts.filter((p) => p !== email && !EMAIL_RE.test(p)).join(" ").trim();
    return { name, email, valid: !!email };
  });
}

export default function HrEmployees() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("single");
  // single
  const [form, setForm] = useState({ name: "", email: "" });
  const [creds, setCreds] = useState(null);
  const [busy, setBusy] = useState(false);
  // bulk
  const [bulkText, setBulkText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  // remove
  const [removeTarget, setRemoveTarget] = useState(null);
  const [removing, setRemoving] = useState(false);
  // budget
  const [budgetTarget, setBudgetTarget] = useState(null);
  const [budgetAmount, setBudgetAmount] = useState("");
  const [savingBudget, setSavingBudget] = useState(false);
  // admins & roles
  const [adminData, setAdminData] = useState(null);
  const [adminOpen, setAdminOpen] = useState(false);
  const [adminForm, setAdminForm] = useState({ name: "", email: "" });
  const [adminBusy, setAdminBusy] = useState(false);
  const [adminCreds, setAdminCreds] = useState(null);
  const [promoteTarget, setPromoteTarget] = useState(null);
  const [demoteTarget, setDemoteTarget] = useState(null);
  const [roleBusy, setRoleBusy] = useState(false);

  const load = () => api.get("/company/employees").then(({ data }) => setData(data)).catch(() => setData({ employees: [], pending_invites: [], budget: null }));
  const loadAdmins = () => api.get("/company/admins").then(({ data }) => setAdminData(data)).catch(() => {});
  useEffect(() => { load(); loadAdmins(); }, []);

  const inviteAdmin = async (e) => {
    e.preventDefault();
    setAdminBusy(true);
    try {
      const { data } = await api.post("/company/admins/invite", adminForm);
      if (data.promoted) {
        toast.success(t("hr.admins.toastPromoted", { name: data.name }));
        setAdminOpen(false); setAdminForm({ name: "", email: "" });
      } else {
        setAdminCreds(data);
        toast.success(t("hr.admins.toastInvited", { name: data.name }));
      }
      loadAdmins(); load();
    } catch (err) { toast.error(apiError(err)); } finally { setAdminBusy(false); }
  };

  const changeRole = async (userId, role) => {
    setRoleBusy(true);
    try {
      const { data } = await api.patch(`/company/members/${userId}/role`, { role });
      toast.success(role === "hr" ? t("hr.admins.toastPromoted", { name: data.name }) : t("hr.admins.toastDemoted", { name: data.name }));
      setPromoteTarget(null); setDemoteTarget(null);
      loadAdmins(); load();
    } catch (err) { toast.error(apiError(err)); } finally { setRoleBusy(false); }
  };

  const closeAdmin = () => { setAdminOpen(false); setAdminCreds(null); setAdminForm({ name: "", email: "" }); };

  const parsed = useMemo(() => parseBulk(bulkText), [bulkText]);
  const validRows = parsed.filter((p) => p.valid);
  const invalidCount = parsed.length - validRows.length;

  const invite = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/company/employees/invite", form);
      setCreds(data);
      setForm({ name: "", email: "" });
      toast.success(t("hr.employees.toastInvited", { name: data.name }));
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const inviteBulk = async () => {
    if (!validRows.length) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/company/employees/invite-bulk", { invites: validRows.map(({ name, email }) => ({ name, email })) });
      setBulkResult(data);
      toast.success(data.skipped_count
        ? t("hr.employees.toastBulkSkipped", { created: data.created_count, skipped: data.skipped_count })
        : t("hr.employees.toastBulk", { created: data.created_count }));
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBulkBusy(false); }
  };

  const importFile = async (fileEl) => {
    const file = fileEl?.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/company/employees/parse-file", fd);
      if (!data.count) {
        toast.error(t("hr.employees.toastNoEmails"));
      } else {
        const lines = data.contacts.map((c) => (c.name ? `${c.name}, ${c.email}` : c.email)).join("\n");
        setBulkText((prev) => (prev.trim() ? prev.trim() + "\n" + lines : lines));
        toast.success(t("hr.employees.toastImported", { count: data.count, file: file.name }));
      }
    } catch (err) { toast.error(apiError(err, t("hr.employees.toastFileError"))); }
    finally { setImporting(false); if (fileEl) fileEl.value = ""; }
  };

  const removeEmployee = async () => {
    setRemoving(true);
    try {
      await api.delete(`/company/employees/${removeTarget.user_id}`);
      toast.success(t("hr.employees.toastRemoved", { name: removeTarget.name }));
      setRemoveTarget(null);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setRemoving(false); }
  };

  const openBudget = (e) => { setBudgetTarget(e); setBudgetAmount(String(e.budget_allocated || 0)); };
  const saveBudget = async () => {
    setSavingBudget(true);
    try {
      await api.patch(`/company/employees/${budgetTarget.user_id}/budget`, { budget_allocated: Number(budgetAmount) || 0 });
      toast.success(t("hr.employees.budgetSaved", { name: budgetTarget.name }));
      setBudgetTarget(null);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setSavingBudget(false); }
  };

  const close = () => {
    setOpen(false); setCreds(null); setForm({ name: "", email: "" });
    setBulkText(""); setBulkResult(null); setMode("single");
  };

  const copyBulkCreds = () => {
    const lines = (bulkResult?.created || []).map((c) => `${c.email}  ${c.temp_password}`).join("\n");
    navigator.clipboard.writeText(lines);
    toast.success(t("hr.employees.toastAllCopied"));
  };

  return (
    <AppShell>
      <PageHeader eyebrow={t("hr.eyebrow")} title={t("hr.employees.title")} description={t("hr.employees.subtitle")}>
        <Dialog open={open} onOpenChange={(o) => (o ? setOpen(true) : close())}>
          <DialogTrigger asChild>
            <Button data-testid="open-invite" className="rounded-lg gap-1.5 active:scale-[0.98] transition-transform"><UserPlus size={16} /> {t("hr.employees.invite")}</Button>
          </DialogTrigger>
          <DialogContent className="rounded-[0.8rem] sm:max-w-lg">
            <DialogHeader><DialogTitle>{t("hr.employees.invite")}</DialogTitle></DialogHeader>

            <Tabs value={mode} onValueChange={(v) => { setMode(v); setCreds(null); setBulkResult(null); }}>
              <TabsList className="grid grid-cols-2 w-full rounded-lg">
                <TabsTrigger value="single" data-testid="mode-single" className="rounded-md gap-1.5"><UserPlus size={14} /> {t("hr.employees.modeSingle")}</TabsTrigger>
                <TabsTrigger value="bulk" data-testid="mode-bulk" className="rounded-md gap-1.5"><ListPlus size={14} /> {t("hr.employees.modeBulk")}</TabsTrigger>
              </TabsList>
            </Tabs>

            {/* SINGLE */}
            {mode === "single" && (creds ? (
              <div className="space-y-4">
                <div className="rounded-lg border border-success/30 bg-success/5 p-4">
                  <p className="text-sm font-medium text-success mb-2">{t("hr.employees.credsShare", { name: creds.name })}</p>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex items-center justify-between bg-card border border-border rounded-md px-3 py-2"><span className="text-muted-foreground">{t("common.email")}</span><span className="font-medium">{creds.email}</span></div>
                    <div className="flex items-center justify-between bg-card border border-border rounded-md px-3 py-2"><span className="text-muted-foreground">{t("hr.employees.tempPassword")}</span><span className="font-mono">{creds.temp_password}</span></div>
                  </div>
                  <Button variant="outline" className="w-full mt-3 rounded-lg gap-1.5" onClick={() => { navigator.clipboard.writeText(`${creds.email} / ${creds.temp_password}`); toast.success(t("common.copied")); }}>
                    <Copy size={14} /> {t("hr.employees.copyCreds")}
                  </Button>
                </div>
                <DialogFooter>
                  <Button variant="outline" className="rounded-lg" onClick={() => setCreds(null)}>{t("hr.employees.inviteAnother")}</Button>
                  <Button className="rounded-lg" onClick={close} data-testid="invite-done">{t("common.done")}</Button>
                </DialogFooter>
              </div>
            ) : (
              <form onSubmit={invite} className="space-y-4">
                <div className="space-y-1.5"><Label>{t("hr.employees.fullName")}</Label><Input data-testid="emp-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jordan Blake" className="h-11 rounded-lg" /></div>
                <div className="space-y-1.5"><Label>{t("hr.employees.workEmail")}</Label><Input data-testid="emp-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="jordan@company.com" className="h-11 rounded-lg" /></div>
                <DialogFooter>
                  <Button data-testid="emp-invite-submit" type="submit" disabled={busy} className="rounded-lg gap-1.5">{busy ? <Spinner /> : <><Mail size={15} /> {t("hr.employees.sendInvite")}</>}</Button>
                </DialogFooter>
              </form>
            ))}

            {/* BULK */}
            {mode === "bulk" && (bulkResult ? (
              <div className="space-y-4">
                {bulkResult.created_count > 0 && (
                  <div className="rounded-lg border border-success/30 bg-success/5 p-4">
                    <p className="text-sm font-medium text-success mb-2 inline-flex items-center gap-1.5"><Check size={15} /> {t("hr.employees.invitedCount", { count: bulkResult.created_count })}</p>
                    <div className="space-y-1.5 max-h-52 overflow-y-auto">
                      {bulkResult.created.map((c) => (
                        <div key={c.email} className="flex items-center justify-between gap-3 bg-card border border-border rounded-md px-3 py-2 text-sm">
                          <span className="min-w-0"><span className="font-medium">{c.name}</span> <span className="text-muted-foreground truncate">· {c.email}</span></span>
                          <span className="font-mono text-xs shrink-0">{c.temp_password}</span>
                        </div>
                      ))}
                    </div>
                    <Button variant="outline" className="w-full mt-3 rounded-lg gap-1.5" data-testid="bulk-copy-all" onClick={copyBulkCreds}><Copy size={14} /> {t("hr.employees.copyAllCreds")}</Button>
                  </div>
                )}
                {bulkResult.skipped_count > 0 && (
                  <div className="rounded-lg border border-warning/30 bg-warning/5 p-4">
                    <p className="text-sm font-medium text-warning mb-2 inline-flex items-center gap-1.5"><AlertTriangle size={15} /> {t("hr.employees.skippedCount", { count: bulkResult.skipped_count })}</p>
                    <div className="space-y-1 text-xs text-muted-foreground max-h-32 overflow-y-auto">
                      {bulkResult.skipped.map((s, i) => (<div key={`${s.email}-${i}`} className="flex justify-between gap-3"><span className="truncate">{s.email}</span><span className="shrink-0">{s.reason}</span></div>))}
                    </div>
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" className="rounded-lg" onClick={() => { setBulkResult(null); setBulkText(""); }}>{t("hr.employees.inviteMore")}</Button>
                  <Button className="rounded-lg" onClick={close} data-testid="bulk-done">{t("common.done")}</Button>
                </DialogFooter>
              </div>
            ) : (
              <div className="space-y-3">
                <input ref={fileRef} type="file" accept=".xlsx,.xlsm,.csv,.docx,.pdf,.txt" hidden onChange={(e) => importFile(e.target)} data-testid="import-file-input" />
                <button
                  type="button"
                  data-testid="import-file-btn"
                  onClick={() => fileRef.current?.click()}
                  disabled={importing}
                  className="w-full rounded-lg border border-dashed border-border bg-secondary/40 hover:border-primary/40 hover:bg-secondary/60 transition-colors p-4 flex items-center gap-3 text-left disabled:opacity-60"
                >
                  <span className="grid place-items-center w-9 h-9 rounded-lg bg-primary/10 text-primary shrink-0">
                    {importing ? <Spinner /> : <Upload size={17} strokeWidth={1.75} />}
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-foreground">{importing ? t("hr.employees.readingFile") : t("hr.employees.importFile")}</span>
                    <span className="block text-xs text-muted-foreground">{t("hr.employees.importHint")}</span>
                  </span>
                  <FileSpreadsheet size={18} className="text-muted-foreground ml-auto shrink-0" />
                </button>
                <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
                  <span className="flex-1 h-px bg-border" /> {t("hr.employees.orPaste")} <span className="flex-1 h-px bg-border" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("hr.employees.listLabel")}</Label>
                  <Textarea
                    data-testid="bulk-textarea"
                    value={bulkText}
                    onChange={(e) => setBulkText(e.target.value)}
                    placeholder={"Jane Doe, jane@company.com\nJohn Smith, john@company.com\nsara@company.com"}
                    className="rounded-lg min-h-40 font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    <Trans i18nKey="hr.employees.formatHint" components={{ b: <span className="font-medium text-foreground" /> }} />
                  </p>
                </div>
                {parsed.length > 0 && (
                  <div className="flex items-center gap-3 text-xs" data-testid="bulk-preview">
                    <span className="inline-flex items-center gap-1 text-success"><Check size={13} /> {t("hr.employees.ready", { count: validRows.length })}</span>
                    {invalidCount > 0 && <span className="inline-flex items-center gap-1 text-warning"><AlertTriangle size={13} /> {t("hr.employees.invalidIgnored", { count: invalidCount })}</span>}
                  </div>
                )}
                <DialogFooter>
                  <Button data-testid="bulk-submit" onClick={inviteBulk} disabled={bulkBusy || !validRows.length} className="rounded-lg gap-1.5">
                    {bulkBusy ? <Spinner /> : <><Mail size={15} /> {t("hr.employees.invitePerson", { count: validRows.length })}</>}
                  </Button>
                </DialogFooter>
              </div>
            ))}
          </DialogContent>
        </Dialog>
      </PageHeader>

      {adminData && <AdminsPanel data={adminData} onInvite={() => { setAdminCreds(null); setAdminForm({ name: "", email: "" }); setAdminOpen(true); }} onDemote={(a) => setDemoteTarget(a)} />}

      {data?.budget && (data.employees?.length > 0) && <BudgetSummary budget={data.budget} />}

      {data === null ? (
        <div className="grid place-items-center py-24"><Spinner className="text-primary w-7 h-7" /></div>
      ) : data.employees.length === 0 ? (
        <EmptyState icon={Users} title={t("hr.employees.emptyTitle")} description={t("hr.employees.emptyDesc")}
          action={<Button className="rounded-lg gap-1.5" onClick={() => setOpen(true)}><UserPlus size={16} /> {t("hr.employees.emptyCta")}</Button>} />
      ) : (
        <div className="card-milled rounded-[0.8rem] overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead>{t("hr.employees.colEmployee")}</TableHead>
                <TableHead className="hidden sm:table-cell">{t("common.email")}</TableHead>
                <TableHead className="min-w-[11rem]">{t("hr.employees.colBudget")}</TableHead>
                <TableHead className="text-center hidden lg:table-cell">{t("hr.employees.colSessions")}</TableHead>
                <TableHead className="hidden xl:table-cell">{t("hr.employees.colJoined")}</TableHead>
                <TableHead className="w-12" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.employees.map((e) => (
                <TableRow key={e.user_id} data-testid={`employee-row-${e.user_id}`} className="border-border hover:bg-secondary/40">
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <UserAvatar src={e.picture} name={e.name} size={36} />
                      <span className="font-medium text-sm">{e.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-sm text-muted-foreground">{e.email}</TableCell>
                  <TableCell><BudgetCell e={e} currency={data.budget?.currency || "EUR"} onEdit={() => openBudget(e)} label={t("hr.employees.setBudget")} /></TableCell>
                  <TableCell className="text-center hidden lg:table-cell"><span className="tabular font-medium">{e.bookings_count}</span></TableCell>
                  <TableCell className="hidden xl:table-cell text-sm text-muted-foreground">{fmtDate(e.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button data-testid={`emp-actions-${e.user_id}`} className="grid place-items-center w-8 h-8 rounded-md text-muted-foreground hover:bg-secondary ml-auto transition-colors">
                          <MoreVertical size={16} />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem data-testid={`emp-promote-${e.user_id}`} onClick={() => setPromoteTarget(e)}>
                          <ShieldCheck size={15} className="mr-2" /> {t("hr.admins.makeAdmin")}
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid={`emp-budget-${e.user_id}`} onClick={() => openBudget(e)}>
                          <Wallet size={15} className="mr-2" /> {t("hr.employees.setBudget")}
                        </DropdownMenuItem>
                        <DropdownMenuItem data-testid={`emp-remove-${e.user_id}`} onClick={() => setRemoveTarget(e)} className="text-destructive focus:text-destructive">
                          <UserMinus size={15} className="mr-2" /> {t("hr.employees.removeFromWorkspace")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Offboarding confirmation */}
      <AlertDialog open={!!removeTarget} onOpenChange={(o) => !o && setRemoveTarget(null)}>
        <AlertDialogContent className="rounded-[0.8rem]">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("hr.employees.removeTitle", { name: removeTarget?.name })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("hr.employees.removeDesc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg" data-testid="remove-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="remove-confirm"
              onClick={(e) => { e.preventDefault(); removeEmployee(); }}
              disabled={removing}
              className="rounded-lg bg-destructive hover:bg-destructive/90"
            >
              {removing ? t("hr.employees.removing") : t("hr.employees.removeConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* Set per-user L&D budget */}
      <Dialog open={!!budgetTarget} onOpenChange={(o) => !o && setBudgetTarget(null)}>
        <DialogContent className="rounded-[0.8rem] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("hr.employees.setBudgetTitle", { name: budgetTarget?.name })}</DialogTitle>
            <DialogDescription>{t("hr.employees.setBudgetDesc")}</DialogDescription>
          </DialogHeader>
          {budgetTarget && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg border border-border p-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{t("hr.employees.budgetUsed")}</p>
                  <p className="text-base font-semibold tabular mt-0.5">{fmtMoney(budgetTarget.budget_used, data?.budget?.currency)}</p>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{t("hr.employees.budgetRemaining")}</p>
                  <p className="text-base font-semibold tabular mt-0.5 text-success">{fmtMoney(budgetTarget.budget_remaining, data?.budget?.currency)}</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>{t("hr.employees.budgetAllocated")}</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">{currencySymbol(data?.budget?.currency)}</span>
                  <Input data-testid="budget-amount" type="number" min="0" value={budgetAmount} onChange={(e) => setBudgetAmount(e.target.value)} className="h-11 rounded-lg pl-7 tabular" autoFocus />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setBudgetTarget(null)}>{t("common.cancel")}</Button>
            <Button data-testid="budget-save" onClick={saveBudget} disabled={savingBudget} className="rounded-lg gap-1.5">{savingBudget ? <Spinner /> : <><Wallet size={15} /> {t("hr.employees.budgetSave")}</>}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite / add administrator */}
      <Dialog open={adminOpen} onOpenChange={(o) => (o ? setAdminOpen(true) : closeAdmin())}>
        <DialogContent className="rounded-[0.8rem] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("hr.admins.inviteTitle")}</DialogTitle>
            <DialogDescription>{t("hr.admins.inviteDesc")}</DialogDescription>
          </DialogHeader>
          {adminCreds ? (
            <div className="space-y-4" data-testid="admin-creds">
              <div className="rounded-lg border border-success/30 bg-success/5 p-4">
                <p className="text-sm font-medium text-success mb-2">{t("hr.admins.credsShare", { name: adminCreds.name })}</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between gap-3"><span className="text-muted-foreground">{t("common.email")}</span><span className="font-medium">{adminCreds.email}</span></div>
                  <div className="flex justify-between gap-3"><span className="text-muted-foreground">{t("hr.employees.tempPassword")}</span><span className="font-mono font-medium">{adminCreds.temp_password}</span></div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="rounded-lg gap-1.5 flex-1" onClick={() => { navigator.clipboard.writeText(`${adminCreds.email}  ${adminCreds.temp_password}`); toast.success(t("common.copied")); }}><Copy size={14} /> {t("hr.employees.copyCreds")}</Button>
                <Button className="rounded-lg flex-1" onClick={closeAdmin}>{t("common.done")}</Button>
              </div>
            </div>
          ) : (
            <form onSubmit={inviteAdmin} className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-sm">{t("hr.admins.nameLabel")}</Label>
                <Input data-testid="admin-name" value={adminForm.name} onChange={(e) => setAdminForm({ ...adminForm, name: e.target.value })} placeholder="Jamie Rivera" className="h-11 rounded-lg" required />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">{t("hr.admins.emailLabel")}</Label>
                <Input data-testid="admin-email" type="email" value={adminForm.email} onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })} placeholder="jamie@company.com" className="h-11 rounded-lg" required />
                <p className="text-xs text-muted-foreground">{t("hr.admins.promoteHint")}</p>
              </div>
              <Button type="submit" data-testid="admin-invite-send" disabled={adminBusy} className="w-full rounded-lg gap-1.5">
                {adminBusy ? <Spinner /> : <><ShieldCheck size={15} /> {t("hr.admins.inviteBtn")}</>}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Promote member -> admin */}
      <AlertDialog open={!!promoteTarget} onOpenChange={(o) => !o && setPromoteTarget(null)}>
        <AlertDialogContent className="rounded-[0.8rem]">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("hr.admins.promoteTitle", { name: promoteTarget?.name })}</AlertDialogTitle>
            <AlertDialogDescription>{t("hr.admins.promoteConfirm")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="promote-confirm" disabled={roleBusy} onClick={() => changeRole(promoteTarget.user_id, "hr")} className="rounded-lg gap-1.5">
              {roleBusy ? <Spinner /> : <>{t("hr.admins.promoteAction")}</>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Demote admin -> member */}
      <AlertDialog open={!!demoteTarget} onOpenChange={(o) => !o && setDemoteTarget(null)}>
        <AlertDialogContent className="rounded-[0.8rem]">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("hr.admins.demoteTitle", { name: demoteTarget?.name })}</AlertDialogTitle>
            <AlertDialogDescription>{t("hr.admins.demoteConfirm")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction data-testid="demote-confirm" disabled={roleBusy} onClick={() => changeRole(demoteTarget.user_id, "employee")} className="rounded-lg gap-1.5 bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {roleBusy ? <Spinner /> : <>{t("hr.admins.demoteAction")}</>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppShell>
  );
}
